<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12" md="3">
          <v-card variant="outlined">
            <v-card-title class="bg-blue-grey-lighten-4 text-subtitle-1">
              Production Completion
            </v-card-title>
            <v-card-text class="pt-4">
              <v-autocomplete
                v-model="form.production_requisition_master_id"
                :items="eligibleRequisitions"
                :item-title="requisitionTitle"
                item-value="id"
                label="Production Requisition *"
                variant="outlined"
                density="compact"
                clearable
                :disabled="isEdit"
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
                @update:model-value="changeRequisition"
              />

              <v-autocomplete
                v-model="form.requisition_receive_master_id"
                :items="availableReceipts"
                :item-title="receiptTitle"
                item-value="id"
                label="Material Receipt (optional)"
                variant="outlined"
                density="compact"
                clearable
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
              />

              <v-date-input
                v-model="form.production_date"
                label="Production Date *"
                input-format="dd/mm/yyyy"
                prepend-icon=""
                prepend-inner-icon="mdi-calendar"
                variant="outlined"
                density="compact"
              />

              <v-autocomplete
                v-model="form.warehouse_id"
                :items="warehouses"
                item-title="name"
                item-value="id"
                label="Production Warehouse (optional)"
                variant="outlined"
                density="compact"
                clearable
                :menu-props="{ contentClass: 'my-custom-dropdown' }"
              />

              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                rows="3"
                variant="outlined"
                density="compact"
              />

              <v-alert type="info" variant="tonal" density="compact">
                Formula consumption follows <strong>Planned Qty</strong>. Actual Finished Qty may differ and is used for yield and finished-good unit cost.
              </v-alert>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="9">
          <v-card variant="outlined">
            <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4 text-subtitle-1">
              <span>Finished Products, Lots &amp; Costing</span>
              <v-spacer />
              <v-chip color="primary" size="small" variant="tonal">
                Estimated Cost: {{ number(grandTotal, 2) }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-4">
              <v-alert v-if="!form.production_requisition_master_id" type="info" variant="tonal">
                Select a requisition after Production has acknowledged the received raw materials.
              </v-alert>

              <v-alert v-else-if="outputs.length === 0" type="warning" variant="tonal">
                This requisition has no remaining planned output quantity.
              </v-alert>

              <v-expansion-panels v-else v-model="openOutputs" multiple>
                <v-expansion-panel
                  v-for="(output, outputIndex) in outputs"
                  :key="output.production_requisition_detail_id"
                  :value="output.production_requisition_detail_id"
                >
                  <v-expansion-panel-title>
                    <div class="d-flex flex-wrap align-center ga-2 w-100 pr-3">
                      <strong>{{ output.product_code }} - {{ output.product_name }}</strong>
                      <v-chip size="x-small" color="indigo" variant="tonal">
                        Template {{ output.template_name || '-' }} v{{ output.template_version_no || 1 }}
                      </v-chip>
                      <v-spacer />
                      <span class="text-caption">
                        Planned {{ number(output.planned_quantity) }} · Actual {{ number(output.quantity) }}
                        · Yield <strong>{{ number(yieldPercent(output), 2) }}%</strong>
                      </span>
                    </div>
                  </v-expansion-panel-title>

                  <v-expansion-panel-text>
                    <v-row dense>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model.number="output.planned_quantity"
                          type="number"
                          min="0.0001"
                          :max="output.max_planned_quantity"
                          step="0.0001"
                          label="Planned / Formula Qty *"
                          density="compact"
                          variant="outlined"
                          @update:model-value="recalculateMaterials(output)"
                        />
                        <div class="text-caption mt-n4 mb-2">Remaining limit: {{ number(output.max_planned_quantity) }}</div>
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model.number="output.quantity"
                          type="number"
                          min="0.0001"
                          step="0.0001"
                          label="Actual Finished Qty *"
                          density="compact"
                          variant="outlined"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model="output.batch_no"
                          label="Finished Batch No."
                          :placeholder="batchHint(output)"
                          :hint="`Leave blank to generate automatically on save: ${batchHint(output)}`"
                          persistent-hint
                          density="compact"
                          variant="outlined"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-date-input
                          v-model="output.manufacturing_date"
                          label="Manufacturing Date *"
                          input-format="dd/mm/yyyy"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          density="compact"
                          variant="outlined"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-date-input
                          v-model="output.expiry_date"
                          label="Expiry Date"
                          input-format="dd/mm/yyyy"
                          prepend-icon=""
                          prepend-inner-icon="mdi-calendar"
                          density="compact"
                          variant="outlined"
                          clearable
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          :model-value="`${number(yieldPercent(output), 2)}%`"
                          label="Yield"
                          density="compact"
                          variant="outlined"
                          readonly
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          :model-value="number(outputMaterialCost(output), 2)"
                          label="Selected Material Cost"
                          density="compact"
                          variant="outlined"
                          readonly
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          :model-value="number(outputTotal(output), 2)"
                          label="Estimated Total Cost"
                          density="compact"
                          variant="outlined"
                          readonly
                        />
                      </v-col>
                    </v-row>

                    <div class="section-title mt-2">Received Material Lot Consumption</div>
                    <v-alert
                      v-if="output.materials.some((material) => material.shortage > 0.0001)"
                      type="error"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                    >
                      One or more materials do not have enough acknowledged/received lot quantity for this planned output.
                    </v-alert>

                    <v-expansion-panels variant="accordion" class="mb-4">
                      <v-expansion-panel
                        v-for="material in output.materials"
                        :key="material.requisition_raw_detail_id"
                      >
                        <v-expansion-panel-title>
                          <div class="d-flex flex-wrap align-center ga-2 w-100 pr-3">
                            <strong>{{ material.product_code }} - {{ material.product_name }}</strong>
                            <v-chip size="x-small" :color="material.item_type === 'packaging' ? 'indigo' : 'teal'">
                              {{ labelize(material.item_type) }}
                            </v-chip>
                            <v-spacer />
                            <span class="text-caption">
                              Std purity {{ number(material.standard_purity, 4) }}% · Target active
                              {{ number(material.target_active, 6) }} · Selected
                              <strong :class="material.shortage > 0.0001 ? 'text-error' : 'text-success'">
                                {{ number(materialSelectedActive(material), 6) }}
                              </strong>
                            </span>
                          </div>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-table density="compact" class="border-sm">
                            <thead>
                              <tr>
                                <th>Lot / Batch</th>
                                <th class="text-center">Purchase Purity</th>
                                <th class="text-center">Expiry</th>
                                <th class="text-end">Received Available</th>
                                <th class="text-end">Unit Cost</th>
                                <th style="width: 150px">Consume Qty</th>
                                <th class="text-end">Active Contribution</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="lot in material.lots" :key="lot.delivery_allocation_id">
                                <td>
                                  <div class="font-weight-medium">{{ lot.lot_no || '-' }}</div>
                                  <div class="text-caption text-medium-emphasis">{{ lot.batch_no || '-' }}</div>
                                </td>
                                <td class="text-center">{{ number(lot.purchase_purity, 4) }}%</td>
                                <td class="text-center">{{ dateOnly(lot.expiry_date) || '-' }}</td>
                                <td class="text-end">{{ number(lot.available_quantity) }}</td>
                                <td class="text-end">{{ number(lot.unit_cost, 6) }}</td>
                                <td>
                                  <v-text-field
                                    v-model.number="lot.quantity"
                                    type="number"
                                    min="0"
                                    :max="lot.available_quantity"
                                    step="0.0001"
                                    density="compact"
                                    variant="outlined"
                                    hide-details
                                    @update:model-value="clampLot(material, lot)"
                                  />
                                </td>
                                <td class="text-end">{{ number(lotActive(material, lot), 6) }}</td>
                              </tr>
                              <tr v-if="material.lots.length === 0">
                                <td colspan="7" class="text-center text-error py-4">
                                  No acknowledged/received lot is available for this requirement.
                                </td>
                              </tr>
                            </tbody>
                          </v-table>
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <div class="d-flex flex-wrap align-center ga-2 section-title">
                      <span>Labour / Overhead / Other Cost</span>
                      <v-spacer />
                      <v-btn size="small" variant="tonal" prepend-icon="mdi-plus" @click="addCost(output)">
                        Add Cost
                      </v-btn>
                    </div>

                    <v-table density="compact" class="border-sm">
                      <thead>
                        <tr>
                          <th style="width: 130px">Type</th>
                          <th>Description</th>
                          <th style="min-width: 180px">Employee</th>
                          <th style="min-width: 200px">Credit Ledger</th>
                          <th style="width: 110px">Qty</th>
                          <th style="width: 125px">Rate</th>
                          <th class="text-end">Amount</th>
                          <th style="width: 45px"></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(cost, costIndex) in output.costs" :key="cost.local_id">
                          <td>
                            <v-select
                              v-model="cost.cost_type"
                              :items="costTypes"
                              item-title="title"
                              item-value="value"
                              density="compact"
                              variant="outlined"
                              hide-details
                              :menu-props="{ contentClass: 'my-custom-dropdown' }"
                            />
                          </td>
                          <td>
                            <v-text-field v-model="cost.description" density="compact" variant="outlined" hide-details />
                          </td>
                          <td>
                            <v-autocomplete
                              v-model="cost.employee_id"
                              :items="employees"
                              :item-title="employeeTitle"
                              item-value="id"
                              density="compact"
                              variant="outlined"
                              hide-details
                              clearable
                              :menu-props="{ contentClass: 'my-custom-dropdown' }"
                            />
                          </td>
                          <td>
                            <v-autocomplete
                              v-model="cost.credit_account_head_id"
                              :items="accountHeads"
                              :item-title="accountTitle"
                              item-value="id"
                              density="compact"
                              variant="outlined"
                              hide-details
                              clearable
                              :menu-props="{ contentClass: 'my-custom-dropdown' }"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="cost.quantity"
                              type="number"
                              min="0"
                              step="0.01"
                              density="compact"
                              variant="outlined"
                              hide-details
                              @update:model-value="calculateCost(cost)"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="cost.rate"
                              type="number"
                              min="0"
                              step="0.01"
                              density="compact"
                              variant="outlined"
                              hide-details
                              @update:model-value="calculateCost(cost)"
                            />
                          </td>
                          <td class="text-end font-weight-medium">{{ number(cost.amount, 2) }}</td>
                          <td>
                            <v-btn
                              icon="mdi-delete-outline"
                              size="x-small"
                              color="error"
                              variant="text"
                              @click="output.costs.splice(costIndex, 1)"
                            />
                          </td>
                        </tr>
                        <tr v-if="output.costs.length === 0">
                          <td colspan="8" class="text-center text-medium-emphasis py-3">No additional cost added.</td>
                        </tr>
                      </tbody>
                    </v-table>

                    <div class="text-end mt-3">
                      <v-btn color="error" variant="text" prepend-icon="mdi-delete-outline" @click="removeOutput(outputIndex)">
                        Skip Finished Product
                      </v-btn>
                    </div>
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-card-text>

            <v-divider />
            <v-card-actions class="justify-end pa-4">
              <v-btn variant="tonal" :disabled="saving" @click="submit(0)">Save Draft</v-btn>
              <v-btn color="primary" :loading="saving" @click="submit(1)">Submit for Approval</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

const store = useStore();
const route = useRoute();
const router = useRouter();

const productionId = route.params.id || route.query.id || null;
const isEdit = Boolean(productionId);
const saving = ref(false);
const outputs = ref([]);
const openOutputs = ref([]);
const selectedRequisition = ref(null);
let localCostId = 0;

const form = reactive({
  production_requisition_master_id: null,
  requisition_receive_master_id: null,
  production_date: new Date(),
  warehouse_id: null,
  remarks: "",
});

const eligibleWorkflow = [
  "materials_partially_received",
  "materials_received",
  "production_in_progress",
  "production_completed",
];
const requisitions = computed(() => store.getters.getRawMetarialRequisitionList || []);
const productions = computed(() => store.getters.getPrdProductionList || []);
const receipts = computed(() => store.getters.getRawMetarialRequisitionReceiveList || []);
const warehouses = computed(() => store.getters.getWarehouseList || []);
const employees = computed(() => store.getters.getEmployeeList || []);
const accountHeads = computed(() => store.getters.getAccountHeads || []);

const eligibleRequisitions = computed(() => requisitions.value.filter((item) => {
  const approved = String(item.approval_status || "").toLowerCase() === "approved";
  return approved && eligibleWorkflow.includes(item.workflow_status);
}));

const availableReceipts = computed(() => receipts.value.filter((item) => {
  const deliveryRequisitionId = item.delivery_master?.requisition_master_id;
  const approved = String(item.approval_status || "").toLowerCase() === "approved";
  return Number(deliveryRequisitionId) === Number(form.production_requisition_master_id)
    && approved
    && Boolean(item.posted_at);
}));

const costTypes = [
  { title: "Labour", value: "labour" },
  { title: "Overhead", value: "overhead" },
  { title: "Other", value: "other" },
];

const grandTotal = computed(() => outputs.value.reduce((sum, output) => sum + outputTotal(output), 0));

function number(value, digits = 4) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: digits });
}

function dateOnly(value) {
  return value ? String(value).slice(0, 10) : "";
}

function labelize(value) {
  return String(value || "-").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function batchHint(output) {
  const words = String(output.product_name || '').match(/[\p{L}\p{N}]+/gu) || [];
  const initials = words.map((word) => [...word][0].toUpperCase()).join('') || 'FG';
  const rawDate = formattedDate(output.manufacturing_date || form.production_date) || '';
  const [year, month, day] = rawDate.split('-');
  const datePart = year && month && day ? `${day}${month}${year}` : 'ddMMyyyy';
  return `${initials}-${datePart}-${productionId || 'Production ID'}`;
}

function requisitionTitle(item) {
  const order = item.sale_order?.code ? ` · ${item.sale_order.code}` : "";
  return `${item.code}${order} · ${labelize(item.workflow_status)}`;
}

function receiptTitle(item) {
  return `${item.code} · ${item.receive_date || ""} · ${item.delivery_master?.code || ""}`;
}

function employeeTitle(item) {
  return `${item.name || item.full_name || ""}${item.code ? ` - ${item.code}` : ""}`;
}

function accountTitle(item) {
  return `${item.name || item.title || ""}${item.code ? ` - ${item.code}` : ""}`;
}

function committedPlanned(detailId) {
  return productions.value
    .filter((production) => Number(production.production_requisition_master_id) === Number(form.production_requisition_master_id))
    .filter((production) => Number(production.id) !== Number(productionId))
    .filter((production) => Number(production.status) === 1 && String(production.approval_status || "") !== "Cancelled")
    .flatMap((production) => production.details || [])
    .filter((detail) => Number(detail.production_requisition_detail_id) === Number(detailId))
    .reduce((sum, detail) => sum + Number(detail.planned_quantity || 0), 0);
}

function availableAllocations(rawDetailId) {
  return (selectedRequisition.value?.deliveries || [])
    .filter((delivery) => Boolean(delivery.posted_at) && String(delivery.approval_status || "").toLowerCase() === "approved")
    .flatMap((delivery) => delivery.allocations || [])
    .filter((allocation) => Number(allocation.requisition_raw_detail_id) === Number(rawDetailId))
    .map((allocation) => ({
      delivery_allocation_id: allocation.id,
      inventory_master_id: allocation.inventory_master_id,
      lot_no: allocation.lot_no,
      batch_no: allocation.batch_no,
      manufacturing_date: allocation.manufacturing_date,
      expiry_date: allocation.expiry_date,
      purchase_purity: Number(allocation.purchase_purity || 0),
      standard_purity: Number(allocation.standard_purity || 0),
      unit_cost: Number(allocation.unit_cost || 0),
      available_quantity: Math.max(
        0,
        Number(allocation.received_quantity || 0)
          - Number(allocation.consumed_quantity || 0)
          - Number(allocation.returned_quantity || 0)
      ),
      quantity: 0,
    }))
    .filter((allocation) => allocation.available_quantity > 0.0001)
    .sort((a, b) => {
      const expiryA = dateOnly(a.expiry_date) || "9999-12-31";
      const expiryB = dateOnly(b.expiry_date) || "9999-12-31";
      return expiryA.localeCompare(expiryB) || Number(a.delivery_allocation_id) - Number(b.delivery_allocation_id);
    });
}

function purityFactor(material, lot) {
  if (material.item_type === "packaging" || material.calculation_method === "balancer") return 1;
  return Number(material.standard_purity || 0) > 0 ? Number(lot.purchase_purity || 0) / 100 : 1;
}

function lotActive(material, lot) {
  return Number(lot.quantity || 0) * purityFactor(material, lot);
}

function materialSelectedActive(material) {
  return material.lots.reduce((sum, lot) => sum + lotActive(material, lot), 0);
}

function allocateMaterial(material) {
  let remaining = Number(material.target_active || 0);
  material.lots.forEach((lot) => {
    const factor = purityFactor(material, lot);
    const quantity = factor > 0 ? Math.min(Number(lot.available_quantity), remaining / factor) : 0;
    lot.quantity = Number(Math.max(0, quantity).toFixed(4));
    remaining = Math.max(0, remaining - lot.quantity * factor);
  });
  material.shortage = Number(remaining.toFixed(6));
}

function buildMaterial(requirement, output) {
  const scale = Number(output.requisition_quantity || 0) > 0
    ? Number(output.planned_quantity || 0) / Number(output.requisition_quantity)
    : 0;
  const targetActive = (requirement.item_type === "packaging" || requirement.calculation_method === "balancer")
    ? Number(requirement.total_required_quantity || 0) * scale
    : Number(requirement.active_required_quantity || 0) * scale;
  const material = {
    requisition_raw_detail_id: requirement.id,
    product_id: requirement.raw_product_id,
    product_code: requirement.raw_product?.code || "",
    product_name: requirement.raw_product?.name || "",
    unit_name: requirement.raw_product?.unit?.name || "",
    item_type: requirement.item_type || "raw_material",
    calculation_method: requirement.calculation_method || "fixed",
    standard_purity: Number(requirement.standard_purity ?? requirement.raw_product?.standard_purity ?? 0),
    target_active: Number(targetActive.toFixed(6)),
    shortage: 0,
    lots: availableAllocations(requirement.id),
  };
  allocateMaterial(material);
  return material;
}

function buildOutput(detail) {
  const remaining = Math.max(0, Number(detail.quantity || 0) - committedPlanned(detail.id));
  const output = {
    production_requisition_detail_id: detail.id,
    product_id: detail.product_id,
    product_code: detail.product?.code || "",
    product_name: detail.product?.name || "",
    unit_name: detail.product?.unit?.name || "",
    template_name: detail.template?.name || "",
    template_version_no: detail.template_version_no || detail.template?.version_no || 1,
    requisition_quantity: Number(detail.quantity || 0),
    max_planned_quantity: remaining,
    planned_quantity: remaining,
    quantity: remaining,
    batch_no: "",
    manufacturing_date: form.production_date ? new Date(form.production_date) : new Date(),
    expiry_date: null,
    materials: [],
    costs: [],
  };
  output.materials = (detail.raw_product_details || []).map((requirement) => buildMaterial(requirement, output));
  return output;
}

async function changeRequisition(id) {
  selectedRequisition.value = null;
  outputs.value = [];
  openOutputs.value = [];
  form.requisition_receive_master_id = null;
  if (!id) return;
  try {
    const response = await store.dispatch("getRawMetarialRequisitionById", id);
    selectedRequisition.value = response?.data || response;
    outputs.value = (selectedRequisition.value?.details || [])
      .map(buildOutput)
      .filter((output) => output.max_planned_quantity > 0.0001);
    openOutputs.value = outputs.value.map((output) => output.production_requisition_detail_id);
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production requisition could not be loaded.");
  }
}

function recalculateMaterials(output) {
  output.planned_quantity = Math.min(
    Math.max(0, Number(output.planned_quantity || 0)),
    Number(output.max_planned_quantity || 0)
  );
  const detail = selectedRequisition.value?.details?.find(
    (item) => Number(item.id) === Number(output.production_requisition_detail_id)
  );
  output.materials = (detail?.raw_product_details || []).map((requirement) => buildMaterial(requirement, output));
}

function clampLot(material, lot) {
  lot.quantity = Math.min(
    Math.max(0, Number(lot.quantity || 0)),
    Number(lot.available_quantity || 0)
  );
  material.shortage = Math.max(0, Number(material.target_active) - materialSelectedActive(material));
}

function yieldPercent(output) {
  return Number(output.planned_quantity || 0) > 0
    ? Number(output.quantity || 0) / Number(output.planned_quantity) * 100
    : 0;
}

function outputMaterialCost(output) {
  return output.materials.reduce((total, material) => total + material.lots.reduce(
    (sum, lot) => sum + Number(lot.quantity || 0) * Number(lot.unit_cost || 0),
    0
  ), 0);
}

function outputAdditionalCost(output) {
  return output.costs.reduce((sum, cost) => sum + Number(cost.amount || 0), 0);
}

function outputTotal(output) {
  return outputMaterialCost(output) + outputAdditionalCost(output);
}

function calculateCost(cost) {
  cost.quantity = Math.max(0, Number(cost.quantity || 0));
  cost.rate = Math.max(0, Number(cost.rate || 0));
  cost.amount = Number((cost.quantity * cost.rate).toFixed(2));
}

function newCost(type = "overhead") {
  return {
    local_id: ++localCostId,
    cost_type: type,
    description: type === "labour" ? "Direct labour" : "",
    employee_id: null,
    credit_account_head_id: null,
    quantity: 1,
    rate: 0,
    amount: 0,
  };
}

function addCost(output) {
  output.costs.push(newCost());
}

function removeOutput(index) {
  outputs.value.splice(index, 1);
}

function validate() {
  if (!form.production_requisition_master_id || !form.production_date) {
    return "Production requisition and Production date are required.";
  }
  if (!outputs.value.length) return "At least one finished-product line is required.";

  for (const output of outputs.value) {
    if (Number(output.planned_quantity || 0) <= 0 || Number(output.quantity || 0) <= 0) {
      return `${output.product_name}: planned and actual finished quantities must be greater than zero.`;
    }
    if (Number(output.planned_quantity) > Number(output.max_planned_quantity) + 0.0001) {
      return `${output.product_name}: planned quantity exceeds the requisition balance.`;
    }
    const manufacturing = formattedDate(output.manufacturing_date);
    const production = formattedDate(form.production_date);
    if (!manufacturing || manufacturing > production) {
      return `${output.product_name}: manufacturing date cannot be after Production date.`;
    }
    if (output.expiry_date && formattedDate(output.expiry_date) <= manufacturing) {
      return `${output.product_name}: expiry date must be after manufacturing date.`;
    }
    for (const material of output.materials) {
      if (materialSelectedActive(material) + 0.0001 < Number(material.target_active || 0)) {
        return `${output.product_name}: insufficient selected received lots for ${material.product_name}.`;
      }
    }
  }
  return null;
}

function productionPayload(status) {
  return {
    production_requisition_master_id: form.production_requisition_master_id,
    requisition_receive_master_id: form.requisition_receive_master_id || null,
    production_date: formattedDate(form.production_date),
    warehouse_id: form.warehouse_id || null,
    status,
    remarks: form.remarks || null,
    cart: outputs.value.map((output) => ({
      production_requisition_detail_id: output.production_requisition_detail_id,
      product_id: output.product_id,
      planned_quantity: Number(output.planned_quantity),
      quantity: Number(output.quantity),
      batch_no: output.batch_no?.trim() || null,
      manufacturing_date: formattedDate(output.manufacturing_date),
      expiry_date: output.expiry_date ? formattedDate(output.expiry_date) : null,
      materials: output.materials.flatMap((material) => material.lots
        .filter((lot) => Number(lot.quantity || 0) > 0)
        .map((lot) => ({
          delivery_allocation_id: lot.delivery_allocation_id,
          quantity: Number(lot.quantity),
        }))),
      costs: output.costs
        .filter((cost) => Number(cost.amount || 0) > 0)
        .map((cost) => ({
          cost_type: cost.cost_type,
          description: cost.description || null,
          employee_id: cost.employee_id || null,
          credit_account_head_id: cost.credit_account_head_id || null,
          quantity: Number(cost.quantity || 0),
          rate: Number(cost.rate || 0),
          amount: Number(cost.amount || 0),
        })),
    })),
  };
}

async function submit(status) {
  const validationMessage = validate();
  if (validationMessage) {
    toast.warning(validationMessage);
    return;
  }
  saving.value = true;
  try {
    const payload = productionPayload(status);
    if (isEdit) payload.id = productionId;
    const response = await store.dispatch(isEdit ? "updatePrdProduction" : "savePrdProduction", payload);
    toast.success(response?.data?.message || "Production completion saved.");
    router.push({ name: "production-list" });
  } catch (error) {
    const errors = error?.response?.data?.errors;
    const text = errors ? Object.values(errors).flat().join("\n") : error?.response?.data?.message;
    toast.error(text || "Production completion could not be saved.");
  } finally {
    saving.value = false;
  }
}

function applyEditDetails(data) {
  form.requisition_receive_master_id = data.requisition_receive_master_id || null;
  form.production_date = data.production_date ? new Date(data.production_date) : new Date();
  form.warehouse_id = data.warehouse_id || null;
  form.remarks = data.remarks || "";

  outputs.value = (data.details || []).map((saved) => {
    const requisitionDetail = selectedRequisition.value?.details?.find(
      (item) => Number(item.id) === Number(saved.production_requisition_detail_id)
    );
    const output = buildOutput(requisitionDetail || {
      id: saved.production_requisition_detail_id,
      product_id: saved.product_id,
      quantity: saved.planned_quantity,
      product: saved.product,
      template: saved.template,
      raw_product_details: [],
    });
    output.planned_quantity = Number(saved.planned_quantity || 0);
    output.quantity = Number(saved.actual_quantity ?? saved.quantity ?? 0);
    output.batch_no = saved.batch_no || "";
    output.manufacturing_date = saved.manufacturing_date ? new Date(saved.manufacturing_date) : new Date(form.production_date);
    output.expiry_date = saved.expiry_date ? new Date(saved.expiry_date) : null;
    recalculateMaterials(output);

    const consumptionByAllocation = new Map(
      (saved.material_consumptions || []).map((item) => [Number(item.delivery_allocation_id), Number(item.quantity || 0)])
    );
    output.materials.forEach((material) => {
      material.lots.forEach((lot) => {
        if (consumptionByAllocation.has(Number(lot.delivery_allocation_id))) {
          lot.quantity = consumptionByAllocation.get(Number(lot.delivery_allocation_id));
        } else {
          lot.quantity = 0;
        }
      });
      material.shortage = Math.max(0, material.target_active - materialSelectedActive(material));
    });
    output.costs = (saved.cost_details || []).map((cost) => ({
      local_id: ++localCostId,
      cost_type: cost.cost_type,
      description: cost.description || "",
      employee_id: cost.employee_id || null,
      credit_account_head_id: cost.credit_account_head_id || null,
      quantity: Number(cost.quantity || 0),
      rate: Number(cost.rate || 0),
      amount: Number(cost.amount || 0),
    }));
    return output;
  });
  openOutputs.value = outputs.value.map((output) => output.production_requisition_detail_id);
}

async function loadEditData() {
  const response = await store.dispatch("getPrdProductionById", productionId);
  const data = response?.data || response;
  form.production_requisition_master_id = data.production_requisition_master_id;
  await changeRequisition(data.production_requisition_master_id);
  applyEditDetails(data);
}

onMounted(async () => {
  const tasks = await Promise.allSettled([
    store.dispatch("getRawMetarialRequisitionList"),
    store.dispatch("getRawMetarialRequisitionReceiveList"),
    store.dispatch("getPrdProductionList"),
    store.dispatch("getWarehouseList"),
    store.dispatch("getEmployees", { is_all_branch: false }),
    store.dispatch("getAccountHeads", { account_type: "LEDGER" }),
  ]);
  if (tasks[0].status === "rejected") {
    toast.error("Production requisitions could not be loaded.");
    return;
  }
  if (isEdit) {
    try {
      await loadEditData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Production draft could not be loaded.");
    }
  }
});
</script>

<style scoped>
.section-title {
  background: #eceff1;
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 8px;
  padding: 8px 10px;
}
</style>
